import { ProblemDetails } from "../types/ProblemDetails";

/**
 * Simplified wrapper of the Fetch API response object.
 */
export interface ResponseDetails<T> {
    /**
     * JSON parsed object of the response body if `response.ok`
     */
    data?: T;

    /**
     * RFC 7807 Problem Details JSON response if not `response.ok` or a `string` if the response is not JSON. 
     */
    problem?: ProblemDetails | string;

    /**
     * Exception thrown when performing the HTTP request.
     */
    error?: unknown;
}

/**
 * Wraps the Fetch API inside a try-catch block and expects JSON response when ok
 * and RFC 7807 Problem Details JSON response when not ok.
 * @param url
 * @param init
 * @returns `data` when `response.ok`, `problem` when not `response.ok`, and `error` when exception
 */
export async function tryFetchJson<T>(url: RequestInfo | URL, init: RequestInit): Promise<ResponseDetails<T>> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
        const response = await fetch(url, {
            ...init,
            signal: controller.signal
        });

        clearTimeout(timeout);

        if (response.ok) {
            return { data: await response.json() };
        }

        try {
            return { problem: await response.json() };
        } catch {
            return { problem: await response.text() };
        }

    } catch (err) {
        clearTimeout(timeout);
        return { error: err };
    }
}
